import {
  LitElement, html, nothing,
  type CSSResultGroup, type PropertyValues, type TemplateResult,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { tabPanelStyles, tabsStyles } from './styles.js';
import { emit } from '../utils/dom.utils.js';

// Internal event — panels fire this when connected so the parent re-syncs.
const PANEL_CONNECTED = 'uwc-tab-panel-connected';

export type TabPosition = 'top' | 'bottom' | 'left' | 'right';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-tab-panel
//
// Declares the content and header metadata for a single tab. The parent
// uwc-tabs reads `header`, `value`, `icon`, `disabled`, and `closable` to
// build the interactive tab list in its own shadow DOM.
//
// Visibility is controlled by the `active` boolean property (set by parent):
//   • active = true  → :host { display: block }  (shows content)
//   • active = false → :host { display: none  }  (hides content)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-tab-panel
 *
 * Declares a single tab and its content. Place inside `<uwc-tabs>`.
 *
 * @attr header   - Tab header label.
 * @attr value    - Unique identifier for this tab (required).
 * @attr icon     - Bootstrap Icons name rendered before the header label.
 * @attr disabled - Tab cannot be selected.
 * @attr closable - Renders a close (×) button on the tab. Press Delete to
 *                  close via keyboard.
 * @attr active   - Set by parent to show/hide this panel. Reflects to attribute.
 *
 * @slot default - Tab panel content.
 *
 * @csspart panel - The panel `<slot>` wrapper.
 */
export class UwcTabPanel extends LitElement {
  static override styles: CSSResultGroup = tabPanelStyles;

  /** Tab header label. */
  @property() header = '';

  /** Unique identifier — used as the `value` in `uwc-change` / `uwc-close` events. */
  @property() value = '';

  /** Bootstrap Icons name rendered before the header label. */
  @property() icon = '';

  /** Prevent the user from selecting this tab. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Show a close (×) button on the tab header. */
  @property({ type: Boolean, reflect: true }) closable = false;

  /**
   * Whether this panel is currently visible.
   * Managed by the parent `uwc-tabs` — do not set manually.
   */
  @property({ type: Boolean, reflect: true }) active = false;

  override connectedCallback() {
    super.connectedCallback();
    // Tell the parent accordion to re-sync its panel list.
    this.dispatchEvent(new CustomEvent(PANEL_CONNECTED, {
      bubbles:  true,
      composed: true,
    }));
  }

  protected override render() {
    return html`<slot part="panel"></slot>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// uwc-tabs
//
// Coordinates a set of uwc-tab-panel children.
//   • Renders the interactive tab list (tab headers) in its own shadow DOM.
//   • Sets `active` on the matching panel to show its content.
//   • Draws a smooth ink-bar indicator using WAAPI-measured positions.
//   • Supports keyboard navigation per WAI-ARIA Tabs pattern.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-tabs
 *
 * A tab container that coordinates `uwc-tab-panel` children.
 *
 * @attr value        - Value of the currently active tab. Auto-selects the
 *                      first non-disabled panel when omitted.
 * @attr scrollable   - Adds prev/next scroll buttons when tabs overflow.
 * @attr tab-position - Where the tab list is rendered relative to the panels.
 *                      `"top"` (default) | `"bottom"` | `"left"` | `"right"`.
 *
 * @slot default - One or more `uwc-tab-panel` elements.
 *
 * @fires uwc-change - Active tab changed. `detail: { value, previousValue }`.
 * @fires uwc-close  - Close button clicked (or Delete pressed on a closable
 *                     tab). `detail: { value }`. Cancelable — call
 *                     `e.preventDefault()` to prevent automatic panel removal.
 *
 * @csspart base      - Root wrapper `<div>`.
 * @csspart nav-wrap  - The strip containing the tab list and scroll buttons.
 * @csspart ink-bar   - The animated active indicator bar.
 * @csspart panels    - The content area `<div>`.
 * @csspart tab       - An individual tab element (role="tab").
 * @csspart tab--active   - Added to the active tab.
 * @csspart tab--disabled - Added to disabled tabs.
 *
 * @cssprop --uwc-tabs-nav-border              - Nav strip border colour.
 * @cssprop --uwc-tabs-tab-padding             - Tab padding (default 0.625rem 1rem).
 * @cssprop --uwc-tabs-tab-gap                 - Gap between icon and label.
 * @cssprop --uwc-tabs-tab-color               - Resting tab text colour.
 * @cssprop --uwc-tabs-tab-hover-color         - Hovered tab text colour.
 * @cssprop --uwc-tabs-tab-hover-bg            - Hovered tab background.
 * @cssprop --uwc-tabs-tab-active-color        - Active tab text colour.
 * @cssprop --uwc-tabs-tab-active-font-weight  - Active tab font weight.
 * @cssprop --uwc-tabs-tab-disabled-color      - Disabled tab text colour.
 * @cssprop --uwc-tabs-tab-font-size           - Tab font size.
 * @cssprop --uwc-tabs-tab-font-weight         - Tab font weight.
 * @cssprop --uwc-tabs-ink-bar-color           - Ink bar colour.
 * @cssprop --uwc-tabs-ink-bar-size            - Ink bar thickness (default 2px).
 * @cssprop --uwc-tabs-panels-padding          - Content area padding.
 * @cssprop --uwc-tabs-vertical-width          - Nav width in left/right mode.
 * @cssprop --uwc-tabs-scroll-btn-bg           - Scroll button background.
 * @cssprop --uwc-tabs-scroll-btn-color        - Scroll button icon colour.
 */
export class UwcTabs extends LitElement {
  static override styles: CSSResultGroup = tabsStyles;

  // ── Public properties ─────────────────────────────────────────────────────

  /** Value of the currently active tab. */
  @property({ reflect: true }) value = '';

  /** Add scroll buttons when the tab list overflows its container. */
  @property({ type: Boolean, reflect: true }) scrollable = false;

  /**
   * Position of the tab list relative to the panel content.
   * `"top"` | `"bottom"` | `"left"` | `"right"`. Default: `"top"`.
   */
  @property({ attribute: 'tab-position', reflect: true })
  tabPosition: TabPosition = 'top';

  // ── Internal state ────────────────────────────────────────────────────────

  @state() private _panels: UwcTabPanel[] = [];

  @query('.uwc-tabs__nav')     private _nav?:    HTMLElement;
  @query('.uwc-tabs__ink-bar') private _inkBar?: HTMLElement;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(PANEL_CONNECTED, this._onPanelConnect as EventListener);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(PANEL_CONNECTED, this._onPanelConnect as EventListener);
  }

  override firstUpdated() {
    this._syncPanels();
    this._initActiveTab();
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (changed.has('value')) {
      this._syncActiveState();
    }
    // Re-measure ink bar position after every render update
    requestAnimationFrame(() => this._updateInkBar());
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Collects direct `uwc-tab-panel` children and stamps ARIA attributes on
   * their host elements so `aria-controls` (in shadow DOM) can reference them
   * by id across the shadow boundary.
   */
  private _syncPanels() {
    this._panels = Array.from(this.children).filter(
      (el): el is UwcTabPanel => el instanceof UwcTabPanel,
    );
    this._panels.forEach(p => {
      // Set id and ARIA on the light-DOM host so cross-shadow ARIA links work
      p.id = `uwc-tabpanel-${p.value}`;
      p.setAttribute('role', 'tabpanel');
      p.setAttribute('tabindex', '0');
      p.setAttribute('aria-labelledby', `uwc-tab-${p.value}`);
    });
  }

  /** Auto-selects the first non-disabled panel if `value` is not set. */
  private _initActiveTab() {
    if (!this.value) {
      const first = this._panels.find(p => !p.disabled);
      if (first) this.value = first.value;
    }
    this._syncActiveState();
    requestAnimationFrame(() => this._updateInkBar());
  }

  /** Sets `active` on every panel to match the current `value`. */
  private _syncActiveState() {
    this._panels.forEach(p => { p.active = p.value === this.value; });
  }

  /**
   * Measures the active tab button's position and sizes the ink bar to match.
   * Runs after every render via `requestAnimationFrame` so layout is complete.
   */
  private _updateInkBar() {
    const inkBar = this._inkBar;
    const nav    = this._nav;
    if (!inkBar || !nav) return;

    const activeEl = this.shadowRoot!.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );
    if (!activeEl) return;

    const isVertical = this.tabPosition === 'left' || this.tabPosition === 'right';

    if (isVertical) {
      inkBar.style.top    = `${activeEl.offsetTop}px`;
      inkBar.style.height = `${activeEl.offsetHeight}px`;
      inkBar.style.left   = '';
      inkBar.style.width  = '';
    } else {
      // Subtract nav scrollLeft so the bar tracks correctly when scrollable
      inkBar.style.left   = `${activeEl.offsetLeft - nav.scrollLeft}px`;
      inkBar.style.width  = `${activeEl.offsetWidth}px`;
      inkBar.style.top    = '';
      inkBar.style.height = '';
    }
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  private _onPanelConnect() {
    this._syncPanels();
    this._initActiveTab();
  }

  private _onSlotChange() {
    this._syncPanels();
    this._initActiveTab();
  }

  /** Make `value` the active tab and fire `uwc-change`. */
  private _selectTab(value: string) {
    if (value === this.value) return;
    const previousValue = this.value;
    this.value = value;
    emit(this, 'uwc-change', { value, previousValue });
  }

  /**
   * Fire `uwc-close` (cancelable). If not prevented, removes the panel from
   * the DOM and selects the next available tab.
   */
  private _closeTab(panelValue: string, e: Event) {
    e.stopPropagation();
    const evt = new CustomEvent('uwc-close', {
      detail:     { value: panelValue },
      bubbles:    true,
      composed:   true,
      cancelable: true,
    });
    this.dispatchEvent(evt);

    if (evt.defaultPrevented) return;

    const panel = this._panels.find(p => p.value === panelValue);
    if (!panel) return;
    panel.remove();
    this._syncPanels();

    // If the closed tab was active, activate the next available tab
    if (this.value === panelValue) {
      const next = this._panels.find(p => !p.disabled);
      this.value = next?.value ?? '';
      if (next) emit(this, 'uwc-change', { value: this.value, previousValue: panelValue });
    }
  }

  /**
   * WAI-ARIA Tabs keyboard pattern:
   *   Arrow Right / Down  — next tab
   *   Arrow Left  / Up    — previous tab
   *   Home                — first tab
   *   End                 — last tab
   *   Delete              — close tab (if closable)
   */
  private _onTabKeydown(e: KeyboardEvent, panelValue: string) {
    const enabled = this._panels.filter(p => !p.disabled);
    const idx     = enabled.findIndex(p => p.value === panelValue);

    const isVertical = this.tabPosition === 'left' || this.tabPosition === 'right';
    const nextKeys   = isVertical ? ['ArrowDown', 'ArrowRight'] : ['ArrowRight', 'ArrowDown'];
    const prevKeys   = isVertical ? ['ArrowUp',   'ArrowLeft']  : ['ArrowLeft',  'ArrowUp'];

    let nextIdx = idx;

    if (nextKeys.includes(e.key)) {
      e.preventDefault();
      nextIdx = (idx + 1) % enabled.length;
    } else if (prevKeys.includes(e.key)) {
      e.preventDefault();
      nextIdx = (idx - 1 + enabled.length) % enabled.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIdx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIdx = enabled.length - 1;
    } else if (e.key === 'Delete') {
      if (enabled[idx]?.closable) {
        e.preventDefault();
        this._closeTab(panelValue, e);
      }
      return;
    } else {
      return;
    }

    const target = enabled[nextIdx];
    if (!target) return;
    this._selectTab(target.value);
    // Shift keyboard focus to the newly activated tab button
    this.shadowRoot!.querySelector<HTMLElement>(`#uwc-tab-${target.value}`)?.focus();
  }

  /** Scroll the tab list left (dir = -1) or right (dir = 1). */
  private _scrollNav(dir: number) {
    this._nav?.scrollBy({ left: dir * 160, behavior: 'smooth' });
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  private _renderTab(panel: UwcTabPanel): TemplateResult {
    const isActive = panel.value === this.value;
    return html`
      <div
        id="uwc-tab-${panel.value}"
        role="tab"
        part="tab ${isActive ? 'tab--active' : ''} ${panel.disabled ? 'tab--disabled' : ''}"
        class=${classMap({
          'uwc-tabs__tab':           true,
          'uwc-tabs__tab--active':   isActive,
          'uwc-tabs__tab--disabled': panel.disabled,
        })}
        tabindex=${isActive ? '0' : '-1'}
        aria-selected=${isActive ? 'true' : 'false'}
        aria-controls="uwc-tabpanel-${panel.value}"
        aria-disabled=${panel.disabled ? 'true' : nothing}
        @click=${panel.disabled ? null : () => this._selectTab(panel.value)}
        @keydown=${(e: KeyboardEvent) => this._onTabKeydown(e, panel.value)}
      >
        ${panel.icon
          ? html`<uwc-icon
                   name=${panel.icon}
                   size="1.05em"
                   class="uwc-tabs__tab-icon"
                 ></uwc-icon>`
          : nothing}
        <span class="uwc-tabs__tab-label">${panel.header || panel.value}</span>
        ${panel.closable ? html`
          <button
            class="uwc-tabs__close-btn"
            tabindex="-1"
            aria-label="Close ${panel.header || panel.value}"
            @click=${(e: MouseEvent) => this._closeTab(panel.value, e)}
          ><uwc-icon name="x-lg" size="0.8em"></uwc-icon></button>
        ` : nothing}
      </div>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    const isVertical = this.tabPosition === 'left' || this.tabPosition === 'right';
    return html`
      <div class="uwc-tabs" part="base">

        <!-- Tab strip: scroll buttons (optional) + tab list + ink bar -->
        <div class="uwc-tabs__nav-wrap" part="nav-wrap">
          ${this.scrollable ? html`
            <button
              class="uwc-tabs__scroll-btn"
              aria-hidden="true"
              tabindex="-1"
              @click=${() => this._scrollNav(-1)}
            ><uwc-icon name="chevron-left"  size="1em"></uwc-icon></button>
          ` : nothing}

          <div class="uwc-tabs__nav-container">
            <div
              class="uwc-tabs__nav"
              role="tablist"
              aria-orientation=${isVertical ? 'vertical' : 'horizontal'}
            >
              ${this._panels.map(p => this._renderTab(p))}
            </div>
            <div class="uwc-tabs__ink-bar" part="ink-bar"></div>
          </div>

          ${this.scrollable ? html`
            <button
              class="uwc-tabs__scroll-btn"
              aria-hidden="true"
              tabindex="-1"
              @click=${() => this._scrollNav(1)}
            ><uwc-icon name="chevron-right" size="1em"></uwc-icon></button>
          ` : nothing}
        </div>

        <!-- Panel content -->
        <div class="uwc-tabs__panels" part="panels">
          <slot @slotchange=${this._onSlotChange}></slot>
        </div>

      </div>
    `;
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'uwc-tabs':      UwcTabs;
    'uwc-tab-panel': UwcTabPanel;
  }
}
